import BaseComponent from "../../../base/base_component";
import FPGamesComponentModel from "./fp_games_component_model";
import ScrollableContainerController from "../../scrollable_container/scrollable_container_controller";
import Device from "../../../utils/device/device";
import Event from '../../../utils/event';
import DOM from "../../../utils/dom";
import * as FP_GAMES_LIST_CONSTS from "./fp_games_component_consts";
import GameItemComponentController from "../../game_item/game_item_component_controller";
import { GAMES_INFO } from "../../../services/games_information/games_information_service_interface";
import { USER_EVENTS, SYSTEM_EVENTS } from "../../../events/events";
import {CyClass} from '../../../../../common/cy-classes';

const gamesView = require('./view/fp_games_component.html');

export default class FPGamesComponentController extends BaseComponent {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'FPGamesComponentController';


    /**
     * @static
     * @type {string}
     */
    static DIRECTIVE = 'cgp-directive-games_list';

    /**
     * Root component selector.
     *
     * @type {string}
     * @private
     */
    _selector = 'cgp-free-play-games-list-component';

    _model: FPGamesComponentModel;
    private _gamesContainer: ScrollableContainerController;
    private _gameComponents: GameItemComponentController[];


    init(params) {
        super.init(params);
        this._model = FPGamesComponentModel.create(params);
    }


    start() {
        const gamesHtml = this.bake(gamesView);
        this.setHTML(gamesHtml);

        /*
            This is a quick hack fix for https://dev.azure.com/888holdings/GamingNDL/_workitems/edit/1630822
            It's a hack mainly because viewportDimensions it's not observed, so if the viewport changes, nothing will happen
         */
        const viewportDimensions = this.serviceFactory.windowService().getWindowInnerDimensions();
        if (this.serviceFactory.integrationService().isMiniOrbit() && viewportDimensions.width >= 768) {
            const windowHeight = viewportDimensions.height;
            DOM.addStyle(this.getDOMElementByClassName("cgp-fp-games-container-and-loader-wrap") as HTMLElement, "height", Math.round(windowHeight / 2) + "px");
        }

        return this;
    }


    /**
     * Handle event when the component attached to DOM.
     * From this point the component is ready to execute DOM manipulation and calculations regarding to entire document
     */
    onAppendedToDOM(): Promise<void> {
        super.onAppendedToDOM();
        //this._subscribeUserEvents();
        //this._assignMessageHeight();
        return this._injectVoucherGames();
    }


    /**
     * Update container with new games
     * @param games
     * @returns {*}
     */
    updateGames(games: number[], fpTypeID: string) {
        //this.disableComponent();
        this._model.setGames(games);
        this._model.setFPType(fpTypeID);
        DOM.removeElement(this.getDOMElementByClassName(FP_GAMES_LIST_CONSTS.FP_GAMES_CONTAINER_CLASS), this._gamesContainer.getEl());
        DOM.removeClass(this.getEl(), 'cgp-fp-games-disabled');
        this._gamesContainer = null;
        return this._injectVoucherGames();
    }


    disableComponent() {
        if (this._gameComponents) {
            this._gameComponents.forEach((component) => {
                component.shutDown();
            });
        }
        DOM.addClass(this.getEl(), 'cgp-fp-games-disabled');
    }

    private _onLoading(isLoading: boolean) {
        if (isLoading) {
            DOM.addClass(this.getEl(), 'cgp-free-play-games-loading');
        } else {
            DOM.removeClass(this.getEl(), 'cgp-free-play-games-loading');
        }
    }

    /**
     * Inject list of voucher's games into the scrollable container
     * @private
     */
    private _injectVoucherGames(): Promise<void> {
        return new Promise((res) => {
            this._onLoading(true);
            // this games are in recommended order
            const games = this._model.getGames();

            // reset to limited view
            this._model.setHasLimit(true);

            this.serviceFactory.gamesInformationService().getGamesInfo(games)
                .then((mappedGames) => {
                    return this._createGameComponents(games, mappedGames);
                })
                .then((gameComponents) => {
                    this._gameComponents = [];
                    gameComponents.forEach((gameComponent) => {
                        DOM.addClass(gameComponent.getEl(), FP_GAMES_LIST_CONSTS.FP_GAMES_CONTAINER_CLASS);
                        DOM.addClass(gameComponent.getEl(), CyClass('cy-free-play-game-item'));
                        this._gameComponents.push(gameComponent);
                    });
                    this._gamesContainer = this._createScrollableGamesContainer(
                        this._gameComponents.map(gameComponent => gameComponent.getEl()),
                    );

                    const ts: number = performance.now();
                    let requestId: number;

                    const loadingFn = () => {
                        const now = performance.now();
                        if (now - ts > 200) {
                            this._onLoading(false);
                            this._appendScrollableGamesContainer();
                            window.cancelAnimationFrame(requestId);
                            res();
                        } else {
                            requestId = window.requestAnimationFrame(loadingFn);
                        }
                    };

                    loadingFn();
                });
        });
    }


    private _appendScrollableGamesContainer() {
        const gamesContainer = this.getDOMElementByClassName(FP_GAMES_LIST_CONSTS.FP_GAMES_CONTAINER_CLASS); // TODO to consts
        DOM.append(gamesContainer, this._gamesContainer.getEl());
        this._gamesContainer.onAppendedToDOM();
    }


    onGameSelected(gameType: number, title: string) {
        this._model.getContainerRef();
    }


    /**
     * Create game components asynchronously
     * @param gamesInfo
     * @returns GameItemComponentController[]
     * @private
     */
    private _createGameComponents(games: number[], gamesInfo: GAMES_INFO): GameItemComponentController[] {
        const gameComponents: GameItemComponentController[] = [];
        games.forEach((gameType, index) => {
            const gameInfo = gamesInfo[gameType];
            if (gameInfo) {

                const shouldShow = this._model.getHasLimit()? index < FP_GAMES_LIST_CONSTS.FP_GAMES_COMPONENT_LIMIT : true;
                const gameItemComp = GameItemComponentController.create({
                    gameData: Object.assign({ gametypeid: gameType }, gameInfo),
                    container: this,
                    itemClass: FP_GAMES_LIST_CONSTS.FP_GAME_ITEM_CLASSES.GAME_ITEM_FP,
                    fpType: this._model.getFPType(),
                    onClicked: () => {
                        this._model.getContainerRef().onGameSelected(gameInfo.gameType, gameInfo.title);
                    },
                    show: shouldShow
                }).start();

                gameComponents.push(gameItemComp);
            }
        });
        return gameComponents;
    }

    private _createShowMoreLessButton(): HTMLElement {
        const buttonCaption = this._getMoreOrLessCaption();
        const btnEl = DOM.createElement('button', buttonCaption);
        DOM.addClass(btnEl, [
            'cgp-themes-base-cta',
            'cgp-theme-property-font-size-small-secondary',
            'cgp-theme-property-dark-grey',
            'cgp-theme-property-background-color-darkmain',
            'cgp-fp-games-more-less-btn',
            FP_GAMES_LIST_CONSTS.FP_GAMES_SHOW_MORE_CLASS
        ]);
        this.onDOMEvent(btnEl, USER_EVENTS.ON_CLICK, () => {
            this._sendAnalytics();
            this._toggleMoreLessBtn();
        });
        return btnEl;
    }

    private _toggleMoreLessBtn() {
        const oldHasLimit = this._model.getHasLimit();
        this._model.setHasLimit(!oldHasLimit);

        // update items
        for (let index = FP_GAMES_LIST_CONSTS.FP_GAMES_COMPONENT_LIMIT; index < this._gameComponents.length; index++) {
            const itemComp = this._gameComponents[index];
            itemComp.updateDisplayState(!this._model.getHasLimit());
        }

        const btn = this.getDOMElementByClassName(FP_GAMES_LIST_CONSTS.FP_GAMES_SHOW_MORE_CLASS) as HTMLElement;
        btn.innerText = this._getMoreOrLessCaption();
    }

    _sendAnalytics() {
        const hasLimit = this._model.getHasLimit();
        let analyticsData = {
            category: "Navigation",
            action: "RewardsDrawer",
            label: hasLimit? "See_All" : "Show_Less"
        };

        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticsData);
    }

    private _getMoreOrLessCaption() {
        const translationKey = this._model.getHasLimit() ? "free_play_show_more" : "free_play_show_less";
        const buttonCaption = this.serviceFactory.languageService().translation(translationKey);
        return buttonCaption;
    }

    /**
     * Create ScrollableContainer for vouchers with device and direction configuration
     * @param vouchers
     * @private
     */
    private _createScrollableGamesContainer(items: HTMLElement[]) {
        let allItems = items;

        // if show more/less buttons are not required, simply don't add them to scrollable container
        if (this._model.getGames().length > FP_GAMES_LIST_CONSTS.FP_GAMES_COMPONENT_LIMIT) {
            allItems = allItems.concat(this._createShowMoreLessButton());
        }

        const instance = ScrollableContainerController.create(Object.assign({}, { items: allItems }, this._createContainerStyle()));
        instance.start();
        return instance;
    }

    /**
     * Create style for vouchers container
     * @returns {{direction: (*|string|*)}}
     * @private
     */
    private _createContainerStyle() {
        let style = { direction: this._model.getDirection() };
        if (false === Device.isMobile()) {
            style = Object.assign({}, style, FP_GAMES_LIST_CONSTS.ScrollingContainerStyle.PC[this._model.getDirection()]);
        } else {
            style = Object.assign({}, { isArrowVisible: false }, style, FP_GAMES_LIST_CONSTS.ScrollingContainerStyle.MOBILE[this._model.getDirection()]);
        }
        return style;
    }
}
